import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ShieldCheck, Users, Truck, ClipboardCheck, Award, Clock } from 'lucide-react';

const WhyChooseUsSection = () => {
  const { t, isRTL } = useLanguage();

  const whyChooseUs = [
    { id: 1, title: t('whyUs.quality'), description: t('whyUs.qualityDesc'), icon: ShieldCheck },
    { id: 2, title: t('whyUs.team'), description: t('whyUs.teamDesc'), icon: Users },
    { id: 3, title: t('whyUs.supply'), description: t('whyUs.supplyDesc'), icon: Truck },
    { id: 4, title: t('whyUs.compliance'), description: t('whyUs.complianceDesc'), icon: ClipboardCheck },
  ];

  return (
    <section id="why-us" className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-50`}></div>
      <div className={`absolute bottom-0 ${isRTL ? 'right-0' : 'left-0'} w-96 h-96 bg-red-50 rounded-full blur-3xl opacity-50`}></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className={`space-y-8 ${isRTL ? 'lg:order-2' : ''}`}>
            <div>
              <span className="inline-block px-4 py-1.5 bg-blue-50 text-brand-blue text-sm font-semibold rounded-full mb-4">
                {t('whyUs.badge')}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
                {t('whyUs.title1')}
                <span className="text-brand-red"> {t('whyUs.title2')}</span>
              </h2>
              <p className="text-lg text-slate-600">
                {t('whyUs.description')}
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {whyChooseUs.map((item) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={item.id}
                    className="group p-5 bg-white rounded-2xl border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-red-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      {IconComponent && <IconComponent className="w-6 h-6 text-brand-blue" />}
                    </div>
                    <h3 className="font-semibold text-slate-800 mb-2 group-hover:text-brand-blue transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-slate-600">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Visual Element */}
          <div className={`relative ${isRTL ? 'lg:order-1' : ''}`}>
            <div className="bg-white rounded-3xl shadow-2xl shadow-slate-200/50 p-8 border border-slate-100">
              <div className="space-y-6">
                {/* Trust Indicators */}
                <div className="flex items-center gap-4 p-4 bg-blue-50/50 rounded-xl">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-7 h-7 text-brand-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">{t('whyUs.trusted')}</h4>
                    <p className="text-sm text-slate-600">{t('whyUs.trustedDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-red-50/50 rounded-xl">
                  <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-7 h-7 text-brand-red" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">{t('whyUs.delivery')}</h4>
                    <p className="text-sm text-slate-600">{t('whyUs.deliveryDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 bg-blue-50/50 rounded-xl">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-7 h-7 text-brand-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">{t('whyUs.guaranteed')}</h4>
                    <p className="text-sm text-slate-600">{t('whyUs.guaranteedDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className={`absolute -z-10 -top-4 ${isRTL ? '-left-4' : '-right-4'} w-full h-full bg-gradient-to-br from-blue-100/50 to-red-100/30 rounded-3xl`}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
